import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {SelectProps, Spin, Tooltip} from 'antd';
import {merge} from 'lodash';

import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import LeadDropdownFilter from '@/components/Filters/LeadDropdownFilter';
import {useDispatch, useModel} from 'umi';

import {AssigneeModel} from '@/typings/models/Assignee';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import styles from './index.less';
import AgentName from '@/components/Agent/AgentName';
import {useLeadAssigneeOptions} from '@/contexts/LeadAssigneeOptionsContext';
import {fetchAssignees} from '@/services/api/lead';
import PERMISSIONS from '@/constants/permissions';

type NativeOptionMeta = { isLabel?: boolean };

type ILeadAssigneeDropdownFiltersProps = {
  filterMode?: string,
  assigneeObject?: AssigneeModel,
  overrideOptionRender?: (data: any, meta: NativeOptionMeta) => React.ReactNode,
  onChange?: (assigneeId: string, assignee?: AssigneeModel) => void,
  onSelectionChange?: (assigneeId: string) => void,
  showUnassigned?: boolean,
  showCreate?: boolean,
  showCreateWhenEmpty?: boolean,
  hideDisabled?: boolean,
  onlyAssignable?: boolean,
  defaultMe?: boolean,
  defaultValue?: string | string[],
  autoFocus?: boolean,
  value?: string | string[]
  selectProps?: SelectProps,
  type?: 'tree' | 'dropdown'
  mode?: 'multiple' | 'default'
  closeContainerPopover?: () => void,
  className?: string,
  style?: any,
  placeholder?: any,
  filterKey?: string,
  strongPlaceholder?: boolean,
  hideIfOnlyMe?: boolean,
  excludeOptions?: string[],
}

const LeadAssigneeDropdownFilters = (props: ILeadAssigneeDropdownFiltersProps) => {
  const { initialState } = useModel('@@initialState');
  const user = initialState.currentUser?.User;
  const [localAgentsList, setLocalAgentsList] = useState<AssigneeModel[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [paginated, setPaginated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedAssignee, setSelectedAssignee] = useState<AssigneeModel | undefined>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const dispatch = useDispatch();

  // Use the optimized context instead of local calculations
  const {
    getOptions,
    getUnassignedOption,
    getCreateAssigneeOption,
    getCachedAssigneeById,
    rawAssignees,
    updatedAt,
    setCachedAssignee,
  } = useLeadAssigneeOptions();

  const onCreate = useCallback(async (searchValue?: string) => {
    props.closeContainerPopover && props.closeContainerPopover();
    return dispatch({
      type: 'user/toggleDrawer',
      payload: {
        initialValues: { searchValue },
        notOpenUserDetailsDrawer: true,
        onCreate: (createResponse: any) => {
          const createdUser = get(createResponse, 'data.data');
          if (createdUser?.id && props.onChange) {
            if (props.mode === 'multiple') {
              const currentValue = Array.isArray(props.value) ? props.value : (props.value ? [props.value] : []);
              props.onChange([...currentValue, createdUser.id] as any, [...(Array.isArray(props.assigneeObject) ? props.assigneeObject : (props.assigneeObject ? [props.assigneeObject] : [])), createdUser] as any);
            } else {
              props.onChange(createdUser.id as any, createdUser);
            }
          }
        },
      },
    });
  }, [props.closeContainerPopover, props.onChange, props.mode, props.value, props.assigneeObject, dispatch]);

  // Get options from context with current props configuration
  const options = useMemo(() => {
    let list = [];
    if (searchText || paginated)
      list = localAgentsList;
    else {
      const optionsFromContext = getOptions({
        hideDisabled: props.hideDisabled ?? false,
        showUnassigned: props.showUnassigned ?? false,
        showCreate: false, // Moved to footer button
        onlyAssignable: props.onlyAssignable ?? false,
      });
      setLocalAgentsList(optionsFromContext);
      list = optionsFromContext;
    }
    if (props.excludeOptions && props.excludeOptions.length > 0)
      return list.filter(option => {
        const id = option.id || (option as any).alias;
        return !props.excludeOptions?.includes(id);
      });

    return list;

  }, [
    updatedAt,
    searchText,
    paginated,
    localAgentsList,
    props.excludeOptions,
    props.hideDisabled,
    props.showUnassigned,
    props.onlyAssignable,
    getOptions,
  ]);


  const searchTimeoutRef = useRef<number | null>(null);
  const loadMoreTimeoutRef = useRef<number | null>(null);

  const fetchData = useCallback((mode: 'search' | 'loadMore', searchQuery?: string) => {
    // Handle search mode
    if (mode === 'search') {
      const searchText = searchQuery || '';
      setSearchText(searchText);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        if (!searchText) {
          setLocalAgentsList([]);
          setCurrentPage(1);
          setHasMore(true);
          setPaginated(false);
          return;
        }

        // Reset pagination when starting new search
        setCurrentPage(1);
        setHasMore(true);
        setPaginated(true);
        setLoading(true);

        const query: any = {
          search: searchText,
          active: props.hideDisabled ? 'only_active' : 'all',
          in_scope: 1,
          page_size: 50,
          page: 1,
        }
        if (props.onlyAssignable)
          query.only_assignable = 1

        fetchAssignees({
          query,
        }).then(response => {
          const fetchedAssignees = get(response, 'data.data', []) as AssigneeModel[];
          setLocalAgentsList(fetchedAssignees);
          setCachedAssignee(fetchedAssignees);
          setHasMore(response.data?.total_pages > response.data?.current_page);
        }).finally(() => setLoading(false));
      }, 300);
      return;
    }

    // Handle loadMore mode
    if (mode === 'loadMore') {
      if (!hasMore || loading) return;

      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }

      loadMoreTimeoutRef.current = setTimeout(() => {
        setLoading(true);
        setPaginated(true);
        const nextPage = currentPage + 1;

        fetchAssignees({
          query: {
            search: searchText,
            active: props.hideDisabled ? 'only_active' : 'all',
            only_assignable: props.onlyAssignable ? '1' : '0',
            in_scope: 1,
            page_size: 50,
            page: nextPage,
          },
        }).then(response => {
          const fetchedAssignees = get(response, 'data.data', []) as AssigneeModel[];

          if (fetchedAssignees.length > 0) {
            setLocalAgentsList(prev => [...prev, ...fetchedAssignees]);
            setCurrentPage(nextPage);
            setHasMore(response.data?.total_pages > response.data?.current_page);
          } else {
            setHasMore(false);
          }
        }).finally(() => {
          setLoading(false);
        });
      }, 150);
    }
  }, [hasMore, loading, searchText, currentPage, props.hideDisabled]);

  const handleSearch = useCallback((searchText: string) => {
    fetchData('search', searchText);
  }, [fetchData]);

  const loadMore = useCallback(() => {
    fetchData('loadMore');
  }, [fetchData]);

  const handlePopupScroll = useCallback((e: any) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight > 0.8 * target.scrollHeight) {
      loadMore();
    }
  }, [loadMore]);

  const UNASSIGNED_OPTION = getUnassignedOption();
  const CREATE_ASSIGNEE_OPTION = getCreateAssigneeOption();

  // A default is applied at most once. Re-running it whenever `selectedAssignee`
  // goes empty again would silently put the value back after the user cleared it.
  const defaultAppliedRef = useRef(false);

  useEffect(() => {
    if (defaultAppliedRef.current) return;
    if (!isEmpty(selectedAssignee) || !isEmpty(props.value)) return;

    if (props.defaultValue) {
      const defaultAgent = rawAssignees.find(assignee => assignee.id === props.defaultValue);
      if (!defaultAgent) return;
      defaultAppliedRef.current = true;
      onChange(defaultAgent.id);
      return;
    }

    // `defaultMe` preselects the current user only when they are one of the offered
    // options — `onlyAssignable`/`hideDisabled` have already narrowed those, so a
    // user who cannot be assigned is deliberately not filled in here.
    if (!props.defaultMe || !user?.alias) return;
    const me = options.find(option => option.id === user.alias);
    if (!me) return;
    defaultAppliedRef.current = true;
    onChange(me.id);
  }, [options, rawAssignees, props.defaultValue, props.defaultMe, props.value, user, selectedAssignee]);

  const resolveOptionRenderForLabel = useCallback((assigneeId?: string | null): AssigneeModel | null => {
    if (props.assigneeObject) return props.assigneeObject;
    return getCachedAssigneeById(assigneeId);
  }, [props.assigneeObject, getCachedAssigneeById, updatedAt]);

  const nativeOptionRender = useCallback((
    { data, key, value }: { data?: AssigneeModel | null; key?: string | null },
    meta?: NativeOptionMeta,
  ): React.ReactNode => {

    const optionId = key || value;

    if (props.overrideOptionRender) {
      try {
        const custom = props.overrideOptionRender({ data, key: optionId }, meta);
        if (custom) return custom;
      } catch (e){

      }
    }

    const resolveFromKey = (k?: string | null): AssigneeModel | null | undefined => {
      if (!k) return null;
      if (k === 'null') return UNASSIGNED_OPTION;
      if (k === 'create') return CREATE_ASSIGNEE_OPTION;

      const fromOptions = options.find(agent => k.includes(agent.id));
      if (fromOptions) return fromOptions;

      return null
    };

    const assignee =
      data ??
      resolveFromKey(optionId) ??
      (meta?.isLabel ? resolveOptionRenderForLabel(optionId) : undefined);

    if (!assignee) return <Spin size={"small"}/>;


    return (
      <Tooltip
        placement="topLeft"
        classNames={{ root: styles.assigneeTooltip }}
        title={assignee.email}
      >
          <AgentName agent={assignee} />
      </Tooltip>
    );
  }, [props.assigneeObject, props.overrideOptionRender, options, UNASSIGNED_OPTION, CREATE_ASSIGNEE_OPTION, resolveOptionRenderForLabel, updatedAt]);

  const onChange = (assigneeId: string | string[]) => {
    if (Array.isArray(assigneeId)) {
      const selectedAssignees = (assigneeId as string[])
        .map((id) => {
          const fromOptions = options.find((agent) => agent.id === id);
          if (fromOptions) return fromOptions;
          return getCachedAssigneeById(id);
        })
        .filter(Boolean) as AssigneeModel[];

      props.onSelectionChange && props.onSelectionChange(assigneeId as any);
      props.onChange && props.onChange(assigneeId as any, selectedAssignees as any);
      return;
    }

    let assignee: AssigneeModel | undefined;
    // First try to find in current options for performance
    assignee = options.find((agent) => agent.id === (assigneeId as string));

    // If not found in current options, check the cache
    if (!assignee) {
      assignee = getCachedAssigneeById(assigneeId as string) || undefined;
    }

    props.onSelectionChange && props.onSelectionChange(assigneeId as string);
    props.onChange && props.onChange(assigneeId as any, assignee);
    setSelectedAssignee(assignee);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchText(undefined);
      setLocalAgentsList([]);
      setCurrentPage(1);
      setPaginated(false);
    }
  };
  const handleFilter = (query: any): boolean => {
    if (typeof query === 'string') {
      // Handle string input case
      return true;
    }
    if (query?.option?.meta?.create) return true;
    const option = query?.option || {};
    const input = query?.input || '';
    const searchable = `${option.label || ''} ${option.firstname || ''} ${option.lastname || ''} ${option.email || ''}`.toLowerCase();
    return searchable.includes(input.toLowerCase());
  };


  if (props.hideIfOnlyMe && user) {
    if (options.length === 1 && options[0].id === user.alias) return null;
  }
  const matchWidth = options.length > 100;
  return (
    <LeadDropdownFilter
      placeholder={<F id={'entity.agent.singular'} defaultMessage={'Agent'} />}
      nativeOptionRender={nativeOptionRender}
      filterKey={props.filterKey || 'assignee'}
      filterOption={handleFilter}
      options={options}
      onSearch={handleSearch}
      {...props}
      loading={loading}
      onOpenChange={handleOpenChange}
      onCreate={props.showCreate ? onCreate : undefined}
      createEntity={INTL_ENTITY.AGENT}
      createAction={INTL_ACTION.CREATE}
      createPermission={[PERMISSIONS.Users.CREATE, PERMISSIONS.BUSINESS_PARTNER]}
      selectProps={merge(props?.selectProps, {
        popupMatchSelectWidth: matchWidth,
        style: {
          minWidth: 180,
        },
        dropdownRender: (menu: React.ReactNode) => (
          <div>
            <Spin spinning={loading}>{menu}</Spin>
          </div>
        ),
        loading: loading,
        mode: props.mode,
        onPopupScroll: handlePopupScroll,
      })}
      onChange={onChange}
    />
  );
};

export default LeadAssigneeDropdownFilters;
